import { DataGridPremium, GridCallbackDetails, GridColDef, GridEventListener, GridRenderCellParams, GridRenderEditCellParams, GridRowEditStopReasons, GridRowModel, GridRowModes, GridRowModesModel, GridRowParams, useGridApiContext, useGridApiRef } from '@mui/x-data-grid-premium';
import { Status, useFetch } from '../../hooks/useFetch';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { Aircraft, Airline, InflightEntertainment, Oven } from '@aviation/cabinapp-airlinedata-sdk';
import { CircularProgress, IconButton, SvgIcon, Tooltip } from '@mui/material';
import { ReactComponent as CrossIcon } from '../../icons/cross.svg';
import { ReactComponent as CheckmarkIcon } from '../../icons/checkmark.svg';
import { ReactComponent as EditIcon } from '../../icons/edit.svg';
import { toast } from 'react-toastify';
import { put } from 'aws-amplify/api';
import LookupDialog, { LookupItem, LookupKind } from './LookupDialog';
import LookupSelect from './LookupSelect';
import AircraftEditDialog from './AircraftEditDialog';

export interface IAircraftsGridProps {
    filterTerm? : string;
    active : boolean;
}

export interface IAircraftsGridRef {
  clearFilter: () => void;
}

type LookupEditCellProps = GridRenderEditCellParams & {
    options: Array<LookupItem>;
    airlines: Array<Airline>;
    onManage: () => void;
};

const LookupEditCell = (params: LookupEditCellProps) => {
    const apiRef = useGridApiContext();
    const { id, field, value, options, airlines, onManage, row } = params;

    return (
        <LookupSelect
            value={value as Array<LookupItem> ?? []}
            options={options}
            airlines={airlines}
            clientCode={row.ClientCode}
            onManage={onManage}
            onChange={(newValue) => { apiRef.current.setEditCellValue({ id, field, value: newValue }); }}
        />
    );
};

const AircraftsGrid = forwardRef<IAircraftsGridRef, IAircraftsGridProps>((props, ref) => {
    const apiRef = useGridApiRef();
    const [refreshCounter, setRefreshCounter] = useState(0);
    const { status, data = [] } = useFetch<Array<Aircraft>>(`/api/airline/aircraft`, (response) => response, refreshCounter);
    const { data: airlines = [] } = useFetch<Array<Airline>>(`/api/airline/airline`, (response) => response);
    const { data: ifes = [] } = useFetch<Array<InflightEntertainment>>(`/api/airline/ife`, (response) => response, refreshCounter);
    const { data: ovens = [] } = useFetch<Array<Oven>>(`/api/airline/oven`, (response) => response, refreshCounter);
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [editAircraft, setEditAircraft] = useState<Aircraft | undefined>(undefined);
    const [lookupKind, setLookupKind] = useState<LookupKind | undefined>(undefined);
    const [loading] = useState(false);

    const clearFilter = () => {
        apiRef.current.setFilterModel({ items: [] });
    };

    useImperativeHandle(ref, () => ({
        clearFilter,
    }));

    const rowId = (row : any) : string => {
        return `${row.AcReg}`;
    }

    // Active: ValidTo not set or not in the past (compared by calendar day); Inactive: ValidTo in the past
    const isActive = (item : Aircraft) : boolean => {
        if(!item.ValidTo)
            return true;
        const validTo = new Date(item.ValidTo);
        if(isNaN(validTo.getTime()))
            return true;
        return validTo.toISOString().substring(0, 10) >= new Date().toISOString().substring(0, 10);
    };

    const filterByTerm = useCallback((term : string | undefined, item : Aircraft) : Boolean => {
        if(term === undefined || term === '')
            return true;

        const fields = [item.AcReg, item.AcRegDash, item.AcVerCode, item.FleetCode, item.FleetName, item.ClientCode, airlines.find(a => a.Code === item.ClientCode)?.Name, item.Remarks];

        return fields.some(f => f?.toUpperCase().includes(term) ?? false);
    }, [airlines]);

    // const dateGetter = (params: any) : Date | null => {
    //     const value : string | undefined = params.value;
    //     if(!value) return null;
    //     const date = new Date(value);
    //     return isNaN(date.getTime()) ? null : date;
    // };

    // // Date part only, shown in the browser's locale (UTC so the calendar day does not shift)
    // const dateFormatter = (params: any) : string => {
    //     const date : Date | null = params.value;
    //     return date ? date.toLocaleDateString(navigator.language, { timeZone: 'UTC' }) : '';
    // };

    const namesCell = (params: GridRenderCellParams) => {
        const items : Array<LookupItem> = params.value ?? [];
        return items.map(i => i.Name).join(', ');
    };

    const columns: GridColDef[] = [
        {
            field: 'edit', headerName: '', width: 55, sortable: false, filterable: false, disableColumnMenu: true,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title='Edit'>
                    <IconButton color='primary' onClick={() => setEditAircraft(params.row)}>
                        <SvgIcon component={EditIcon} inheritViewBox />
                    </IconButton>
                </Tooltip>
            )
        },
        { field: 'ClientCode', headerName: 'Owner', minWidth: 150, editable: false,
            valueFormatter: (params: any) => airlines.find(a => a.Code === params.value)?.Name ?? params.value },
        { field: 'AcRegDash', headerName: 'Registration', minWidth: 120, editable: false },
        // { field: 'AcVerCode', headerName: 'Version', minWidth: 70, editable: false },
        { field: 'FleetName', headerName: 'Fleet', minWidth: 150, editable: false },
        { field: 'Ife', headerName: 'IFE', minWidth: 250, editable: true, sortable: false, filterable: false,
            renderCell: namesCell,
            renderEditCell: (params: GridRenderEditCellParams) => <LookupEditCell {...params} options={ifes} airlines={airlines} onManage={() => setLookupKind('ife')} />
        },
        { field: 'Ovens', headerName: 'Ovens', minWidth: 120, editable: true, sortable: false, filterable: false,
            renderCell: namesCell,
            renderEditCell: (params: GridRenderEditCellParams) => <LookupEditCell {...params} options={ovens} airlines={airlines} onManage={() => setLookupKind('oven')} />
        },
        { field: 'SkyInterior', headerName: 'Sky Interior', minWidth: 110, type: 'boolean', editable: true },
        { field: 'Connectivity', headerName: 'Connectivity', minWidth: 110, type: 'boolean', editable: true },
        // { field: 'Remarks', headerName: 'Remarks', minWidth: 200, editable: true },
        // { field: 'ValidFrom', headerName: 'Valid From', minWidth: 120, type: 'date', editable: false, valueGetter: dateGetter, valueFormatter: dateFormatter },
        // { field: 'ValidTo', headerName: 'Valid To', minWidth: 120, type: 'date', editable: false, valueGetter: dateGetter, valueFormatter: dateFormatter },
        { field: 'actions', type: 'actions', headerName: '', minWidth: 140, renderCell: ({ row }: Partial<GridRowParams>) => {
            const isInEditMode = (rowModesModel[rowId(row)]?.mode ?? GridRowModes.View) === GridRowModes.Edit;
            const isLoading = row.IsLoading ?? false;

            if(isInEditMode)
                return [
                    <IconButton color="success" onClick={handleSaveClick(row)}>
                        <SvgIcon component={CheckmarkIcon} inheritViewBox />
                    </IconButton>,
                    <IconButton color="error" onClick={handleCancelClick(rowId(row))}>
                        <SvgIcon component={CrossIcon} inheritViewBox />
                    </IconButton>
                ]
            else if(isLoading)
                return (
                    <CircularProgress size={19} />
                )
            }
        }
    ];

    const aggregatedData = useMemo(() => {
        return data.filter(o => isActive(o) === props.active && filterByTerm(props.filterTerm, o)).map(o => {
            return { ...o, id: rowId(o), Ife: o.Ife ?? [], Ovens: o.Ovens ?? [], SkyInterior: o.SkyInterior ?? false, Connectivity: o.Connectivity ?? false }
        }).sort((a,b) => (a.AcReg ?? '') > (b.AcReg ?? '') ? 1 : -1);
    }, [data, props.filterTerm, props.active, filterByTerm]);

    const handleRowModesModelChange = (model: GridRowModesModel, details: GridCallbackDetails) => {
        setRowModesModel(model);
      };

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if(params.reason === GridRowEditStopReasons.rowFocusOut)
            event.defaultMuiPrevented = true;
        else
            setRowModesModel({...rowModesModel, [params.id]: {mode: GridRowModes.View}});

    };

    const handleSaveClick = (item : any) => () => {
        setRowModesModel({...rowModesModel, [rowId(item) ?? '']: {mode: GridRowModes.View }});
    };

    const handleCancelClick = (id : any) => () => {
        setRowModesModel({
          ...rowModesModel,
          [id]: {mode: GridRowModes.View, ignoreModifications: true},
        });
      };

    const updateItem = async (item : any) => {
            let payload : Aircraft = {
                AcReg: item.AcReg,
                Ife: item.Ife ?? [],
                Ovens: item.Ovens ?? [],
                SkyInterior: item.SkyInterior ?? false,
                Connectivity: item.Connectivity ?? false,
                Remarks: item.Remarks ?? '',
            }

            let update : any = payload;

            try {
                await put({ path: `/api/airline/aircraft/${payload.AcReg}`, apiName: 'api', options: { body: {...update}}});

                return true;
            } catch(e) {
                console.error(e);
                return false;
            }
        }

    const processRowUpdate = (row: GridRowModel) => {
        const updatedRow = { ...row, isNew: false, IsLoading: true };

        updateItem(row as Aircraft).then(success => {
            apiRef.current.updateRows([{ id: rowId(row), IsLoading: false }]);

            if(success)
                toast.success(`Changes to aircraft saved successfully.`);
            else
                toast.error(`An error occurred while saving aircraft`);
        })
        return updatedRow;
    };

    return (
        <>
            <DataGridPremium
                apiRef={apiRef}
                autoHeight
                rows={aggregatedData}
                columns={columns}
                editMode='row'
                pagination
                loading={status === Status.Fetching || status === Status.Idle || loading}
                getRowClassName={(params) => { return params.indexRelativeToCurrentPage % 2 === 1 ? `tui-grid-alternate-row` : ''}}
                isCellEditable={() => true }
                rowModesModel={rowModesModel}
                onRowModesModelChange={(m, d) => handleRowModesModelChange(m, d)}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
            />
            {editAircraft && (
                <AircraftEditDialog
                    open
                    aircraft={editAircraft}
                    ifes={ifes}
                    ovens={ovens}
                    airlines={airlines}
                    onSave={updateItem}
                    onSaved={() => setRefreshCounter(c => c + 1)}
                    onManage={(kind) => setLookupKind(kind)}
                    onClose={() => setEditAircraft(undefined)}
                />
            )}
            {lookupKind && (
                <LookupDialog
                    open
                    kind={lookupKind}
                    items={lookupKind === 'ife' ? ifes : ovens}
                    airlines={airlines}
                    aircrafts={data}
                    onClose={() => setLookupKind(undefined)}
                    onChanged={() => setRefreshCounter(c => c + 1)}
                />
            )}
        </>
    );
});

export default AircraftsGrid;
